import { fetchAnalyticsMetrics, fetchCampaignByIdQuery, fetchCampaignLabels } from 'api/queries';
import {
  selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  selectIsDynamicContentReportingSnippetsFeatureFlagOn,
  selectIsEmbeddedMessagingCoreFeatureFlagOn,
  selectIsWebInAppReportingFeatureFlagOn,
} from 'core/userContext';
import { useCampaignsHasRecentTriggeredAlerts } from 'modules/campaigns/lib';
import { AlertsHistorySlideOver } from 'modules/campaigns/lib/components/AlertsHistorySlideOver';
import { CampaignAlertHistorySource } from 'modules/campaigns/lib/components/AlertsHistorySlideOver/types';
import { RECENT_ALERTS_DURATION_WINDOW } from 'modules/campaigns/lib/constants';
import { ExportEventModal } from 'modules/messagingInsights/components/ExportEventModal';
import {
  PROXIED_OPENS_BANNER_CONTENT,
  PROXIED_OPENS_DATE,
} from 'modules/messagingInsights/constants';
import { StyledPage } from 'modules/productAnalytics/Styled';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { InfoBanner } from 'ui/banner';
import { CampaignMissing, ErrorStatePageError } from 'ui/illustrations';
import { SideNav } from 'ui/sideNav';
import { colors } from 'ui/theme';
import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';
import { kebabCase } from 'utils/stringHelpers';
import { getUrlParams } from 'utils/urlParams';
import {
  fetchDestinationsRequest,
  fetchMetricsForCampaignRequest,
  fetchStatsForTimerangeRequest,
  updateAnalyticsMetricOptions,
  updateCampaignData,
  updateSelectedVariant,
} from '../actions';
import {
  CAMPAIGN_ANALYTICS,
  InAppMetricsToBreakdownArr,
  PRE_ACTIVATION_STATES,
  RUNNING,
} from '../constants';
import {
  selectDestinationsByCampaign,
  selectHasStatsForTimeRangeResults,
  selectIsLoading,
} from '../selectors';
import { DateBar } from './DateBar';
import { NoDataCard } from './NoDataCard';
import { PageActions } from './PageActions';
import { PageSubsection } from './PageSubsection';
import {
  ContentBody,
  InfoBannerWrapper,
  MainContentLayout,
  SectionHeader,
  SectionWrapper,
  StyledBannerContainer,
  StyledHeaderContent,
  StyledPageHeader,
  StyledTitle,
} from './Styled';

import { useFiltersContext, useTimeFiltersContext } from '../context';
import { useWhatsAppGAFF } from '../hooks';
import { generateNoDataCopy, generateSideNavLinks } from '../utils';

const getDoubleLine = (expId, variationId, experiments) => {
  const experiment = experiments?.find((el) => el.id === expId) || {};
  return (
    <div>
      <div>{experiment.name}</div>
      <div>{variationId}</div>
    </div>
  );
};

export const LayoutTemplate = ({ canShowFPOBanner, children, contentHeader = <></>, pageName }) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const isDCRFFOn = useSelector(selectIsDynamicContentReportingSnippetsFeatureFlagOn);
  const isWebInAppReportingFeatureFlagOn = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);
  const isCampaignPerformanceAlertsHistoryEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );
  const destinationsByCampaign = useSelector(selectDestinationsByCampaign);
  const { campaignId } = getUrlParams(search);
  const { dateParams, setDateParams } = useTimeFiltersContext();
  const { variantId, setFilterString, filterString } = useFiltersContext();
  const hasData = useSelector(selectHasStatsForTimeRangeResults);
  const isLoading = useSelector(selectIsLoading);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportInAppEventModalOpen, setIsExportInAppEventModalOpen] = useState(false);
  const [isRunningCampaignInfoBannerOpen, setIsRunningCampaignInfoBannerOpen] = useState(false);
  const [isFilteredProxiedOpensInfoBannerOpen, setIsFilteredProxiedOpensInfoBannerOpen] =
    useState(false);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const isWhatsAppGAEnabled = useWhatsAppGAFF();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    data = {},
    error: errorFetchingCampaignInfo,
    refetch,
  } = useGraphql(fetchCampaignByIdQuery, {
    variables: { id: parseInt(campaignId, 10) },
  });

  const { campaign } = data;
  const {
    messageMedium,
    name,
    labels,
    id,
    startAt,
    endedAt,
    campaignState,
    conversions,
    experiments,
    campaignTypeDefinition,
  } = campaign || {};

  const { data: labelData = {} } = useGraphql(fetchCampaignLabels, {
    variables: { id: parseInt(campaignId, 10) },
  });

  const { data: analyticMetrics = {} } = useGraphql(fetchAnalyticsMetrics, {
    variables: { messageMedium, page: CAMPAIGN_ANALYTICS },
  });

  const AnalyticsMetricsOptions = (analyticMetrics?.analyticsMetrics || []).filter(
    (metric) =>
      metric.category &&
      metric.metricName !== 'TotalSMSInbound' &&
      metric.metricName !== 'UniqueSMSInbound',
  );

  const analyticsMetricsOptionsString = JSON.stringify(AnalyticsMetricsOptions);

  const { campaignLabels: suggestedLabels = [] } = labelData;

  const experimentIds = experiments?.map((exp) => parseInt(exp.id, 10)) || [];
  const experimentsVariations = experiments?.flatMap((exp) => exp?.variations || []) || [];
  const experimentsVariationStr = JSON.stringify(
    [...experimentsVariations]?.sort((a, b) => a?.id - b?.id),
  );

  const variantDropdownOptions = [
    {
      id: 'default',
      label: 'All Experiment Variations',
      value: 'default',
    },
    ...experimentsVariations.map((v) => ({
      id: `${v.name}-${v.id}`,
      label: `${v.name}`,
      doubleLine: getDoubleLine(v.experimentId, v.id, experiments),
      value: v.id,
    })),
  ];
  const isCampaignNotActive = PRE_ACTIVATION_STATES.includes(campaignState);
  const isCampaignRunning = campaignState === RUNNING;
  const showNoDataCard = !hasData && (isCampaignNotActive || isCampaignRunning);
  const showRunningCampaignBanner = isCampaignRunning && !showNoDataCard && !isLoading;
  const noDataCopy = generateNoDataCopy(campaignState);

  useEffect(() => {
    dispatch(updateCampaignData(campaign));
    if (!dateParams && campaign) {
      setDateParams({
        startTime: startAt ? moment(startAt) : moment(campaign.createdAt),
        endTime: moment(),
      });
    }
    if (isWebInAppReportingFeatureFlagOn) {
      dispatch(fetchDestinationsRequest());
    }
  }, [campaign]);

  useEffect(() => {
    setIsRunningCampaignInfoBannerOpen(showRunningCampaignBanner);
  }, [showRunningCampaignBanner]);

  useEffect(() => {
    const isEmail = messageMedium === 'Email';

    if (isEmail) {
      const startTime = dateParams?.startTime || startAt || campaign.createdAt;
      const endTime = dateParams?.endTime || endedAt || moment();
      const isFilterProxiedOpenDateInRange =
        startTime >= PROXIED_OPENS_DATE || PROXIED_OPENS_DATE < endTime;

      setIsFilteredProxiedOpensInfoBannerOpen(isFilterProxiedOpenDateInRange && canShowFPOBanner);
    }
  }, [dateParams, messageMedium, canShowFPOBanner]);

  useEffect(() => {
    const valString = [
      JSON.stringify(data),
      JSON.stringify(dateParams),
      variantId,
      experimentsVariationStr,
      analyticsMetricsOptionsString,
    ].join('');
    if (
      valString !== filterString &&
      AnalyticsMetricsOptions?.length > 0 &&
      messageMedium &&
      dateParams &&
      dateParams.endTime?.isAfter(dateParams.startTime)
    ) {
      const selectedVariation = experimentsVariations.find((el) => el.id === variantId) || {};
      const { templateId } = selectedVariation;
      const metricOptions = AnalyticsMetricsOptions.map((el) => ({
        name: el.metricName,
      }));
      setFilterString(valString);
      const inAppBreakdownMetrics =
        isWebInAppReportingFeatureFlagOn && messageMedium === 'InApp'
          ? InAppMetricsToBreakdownArr
          : [];
      dispatch(updateSelectedVariant(selectedVariation));
      dispatch(
        fetchStatsForTimerangeRequest({
          campaignId,
          startTime: dateParams.startTime.toISOString(),
          endTime: dateParams.endTime.toISOString(),
          selectedMetrics: [
            { name: 'CityData' },
            { name: 'DeviceData' },
            { name: 'Conversions' },
            ...metricOptions,
            ...inAppBreakdownMetrics,
          ],
          messageMedium,
          conversions,
          templateId,
        }),
      );
      if (messageMedium === 'Email' || messageMedium === 'Push' || messageMedium === 'SMS') {
        const queryData = {
          startTime: dateParams.startTime.toISOString(),
          endTime: dateParams.endTime.toISOString(),
          selectedMetrics: [
            { name: 'TopBounceReasonsBreakdown' },
            { name: 'SendSkipBreakdown' },
            { name: 'SendSkipReasonsBreakdown' },
            { name: 'UnsubscribeSourcesBreakdown' },
          ],
          campaignId: parseInt(campaignId, 10),
          templateId,
        };
        dispatch(fetchMetricsForCampaignRequest(queryData));
      }
    }
  }, [data, dateParams, variantId, experimentsVariationStr, analyticsMetricsOptionsString]);

  useEffect(() => {
    dispatch(updateAnalyticsMetricOptions(AnalyticsMetricsOptions));
  }, [analyticsMetricsOptionsString]);

  const SIDE_NAV_LINKS = generateSideNavLinks(
    search,
    messageMedium,
    isDCRFFOn,
    isWhatsAppGAEnabled,
  );

  const destinations =
    isWebInAppReportingFeatureFlagOn && messageMedium === 'InApp'
      ? destinationsByCampaign?.map((app) => ({
          name: app.name,
          platform: app.platform,
        }))
      : undefined;

  /** Campaign alerts */
  const { campaignTriggeredAlerts } = useCampaignsHasRecentTriggeredAlerts({
    variables: {
      alertTimeWindow: RECENT_ALERTS_DURATION_WINDOW,
      campaignIds: [parseInt(campaignId, 10)],
    },
    skip: !isCampaignPerformanceAlertsHistoryEnabled,
  });
  const hasRecentTriggeredAlerts = !!campaignTriggeredAlerts?.[0]?.hasRecentAlerts;

  if (errorFetchingCampaignInfo) {
    return (
      <StyledPage>
        <NoDataCard
          Icon={ErrorStatePageError}
          title="We've ran into a snag!"
          subtitle="There was an error loading this campaign's data. Please try refreshing the page or wait a few minutes and try again."
        />
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      {campaign === null || (messageMedium === 'Embedded' && !isEmbeddedMessagingEnabled) ? (
        <NoDataCard
          Icon={CampaignMissing}
          title={'We can’t seem to find this campaign'}
          subtitle={
            'Check the campaign ID in the URL for errors, or try switching to a different project.'
          }
        />
      ) : (
        <>
          <SectionWrapper backgroundColor={colors.white}>
            <StyledPageHeader
              documentTitle={`${name} | Campaign Analytics`}
              pageActions={
                <PageActions
                  campaignMetadata={campaign}
                  onViewAlerts={() => setShowAlertHistory(true)}
                  setIsExportModalOpen={setIsExportModalOpen}
                  setIsExportInAppEventModalOpen={setIsExportInAppEventModalOpen}
                />
              }
              pageSubSection={
                <PageSubsection
                  hasRecentTriggeredAlerts={hasRecentTriggeredAlerts}
                  labels={labels}
                  refetch={refetch}
                  suggestedLabels={suggestedLabels}
                  destinations={destinations}
                  campaignDetails={
                    campaign
                      ? {
                          id,
                          createdAt: campaign?.createdAt,
                          messageMedium,
                          campaignState,
                          campaignTypeDefinition,
                        }
                      : undefined
                  }
                  onViewAlerts={() => setShowAlertHistory(true)}
                />
              }
              documentation={{
                href: 'https://support.iterable.com/hc/en-us/articles/218203223-Viewing-Campaign-Analytics-',
                content: 'Documentation',
              }}
              breadcrumb={{
                href: '/messagingInsights',
                content: 'MESSAGING INSIGHTS',
              }}
            >
              <StyledHeaderContent>
                <StyledTitle>{name || ''}</StyledTitle>
              </StyledHeaderContent>
            </StyledPageHeader>
          </SectionWrapper>
          <StyledBannerContainer>
            <SectionWrapper>
              <DateBar
                campaignStartTime={startAt}
                campaignEndTime={endedAt}
                campaign={campaign}
                variantOptions={variantDropdownOptions}
              />
            </SectionWrapper>
          </StyledBannerContainer>
          <SectionWrapper>
            <MainContentLayout>
              <SideNav linksAndSections={SIDE_NAV_LINKS} isSticky stickyPadding={48} />
              <ContentBody>
                {isRunningCampaignInfoBannerOpen && (
                  <InfoBannerWrapper>
                    <InfoBanner onClose={() => setIsRunningCampaignInfoBannerOpen(false)}>
                      This campaign is still running. Metrics may change as more recipients engage
                      with this campaign.
                    </InfoBanner>
                  </InfoBannerWrapper>
                )}
                {isFilteredProxiedOpensInfoBannerOpen && (
                  <InfoBannerWrapper>
                    <InfoBanner onClose={() => setIsFilteredProxiedOpensInfoBannerOpen(false)}>
                      {PROXIED_OPENS_BANNER_CONTENT}
                    </InfoBanner>
                  </InfoBannerWrapper>
                )}
                {contentHeader}
                {pageName && (
                  <SectionHeader data-test={`ca-header-${kebabCase(pageName)}`}>
                    {pageName}
                  </SectionHeader>
                )}
                {isLoading || !showNoDataCard ? <>{children}</> : <NoDataCard {...noDataCopy} />}
              </ContentBody>
            </MainContentLayout>
          </SectionWrapper>

          {isExportModalOpen && (
            <ExportEventModal
              isExportModalOpen={isExportModalOpen}
              setIsExportModalOpen={setIsExportModalOpen}
              selectedMediums={[messageMedium]}
              selectedIds={[id]}
            />
          )}
          {isExportInAppEventModalOpen && (
            <ExportEventModal
              isExportModalOpen={isExportInAppEventModalOpen}
              setIsExportModalOpen={setIsExportInAppEventModalOpen}
              selectedMediums={[messageMedium]}
              selectedIds={[id]}
              showAppPackage
            />
          )}

          {showAlertHistory && (
            <AlertsHistorySlideOver
              campaignAlertsEnabled={campaign.campaignAlertsEnabled}
              campaignName={campaign.name}
              campaignId={campaign.id}
              templateId={campaign.templateId}
              messageMedium={campaign.messageMedium}
              campaignState={campaign.campaignState}
              campaignType={campaign.campaignTypeDefinition}
              onClose={() => setShowAlertHistory(false)}
              onEditAlerts={noop} // not supporting alert overrides for now
              source={CampaignAlertHistorySource.CampaignAnalytics}
            />
          )}
        </>
      )}
    </StyledPage>
  );
};
